import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Building2, FileText, Heading, MessageSquare, Wallet,
  TrendingUp, Activity, RefreshCw, CheckCircle, XCircle, Clock,
  ArrowUpRight, ArrowDownRight, Shield, Zap, Server, Eye, X, Info, LogIn, Calendar
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import api from '../../../services/api';
import { formatIndianCurrency } from '../../../utils/numberFormat';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

// Skeleton loader component
const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const KPISkeleton = () => (
  <div className="rounded-xl p-4" style={{ background: 'rgba(99, 102, 241, 0.04)', border: '1px solid rgba(99, 102, 241, 0.08)' }}>
    <div className="flex items-center justify-between mb-2">
      <Skeleton className="w-8 h-8 rounded-lg" />
      <Skeleton className="w-16 h-3" />
    </div>
    <Skeleton className="w-20 h-7 mb-2" />
    <Skeleton className="w-14 h-3" />
  </div>
);

const MasterDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [ipPopup, setIpPopup] = useState(null);

  const fetchStats = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      const response = await api.get('/admin/dashboard/master-stats');
      setStats(response.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(() => fetchStats(true), 60000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (error && !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <XCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-red-600 font-medium">{error}</p>
          <button onClick={() => fetchStats()} className="mt-3 px-4 py-2 text-white rounded-lg text-sm" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const s = stats || {};
  const users = s.users || {};
  const entities = s.entities || {};
  const headers = s.headers || {};
  const templates = s.templates || {};
  const sms = s.sms || {};
  const recharge = s.recharge || {};
  const fromCache = s.from_cache;

  const todayTotal = sms.today || 0;
  const todayDeliveryRate = todayTotal > 0 ? ((sms.delivered / todayTotal) * 100).toFixed(1) : 0;
  const todayFailureRate = todayTotal > 0 ? ((sms.failed / todayTotal) * 100).toFixed(1) : 0;

  const channelTotal = (sms.by_channel || []).reduce((sum, ch) => sum + (ch.count || 0), 0);

  const kpiCards = [
    { title: 'Total Users', value: users.total || 0, sub: `${users.active || 0} active`, icon: Users, gradient: 'linear-gradient(135deg, #3b82f6, #60a5fa)', bgColor: 'rgba(59, 130, 246, 0.06)', borderColor: 'rgba(59, 130, 246, 0.12)' },
    { title: 'Entities', value: entities.total || 0, sub: `${entities.active || 0} active`, icon: Building2, gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', bgColor: 'rgba(139, 92, 246, 0.06)', borderColor: 'rgba(139, 92, 246, 0.12)' },
    { title: 'Headers', value: headers.total || 0, sub: `${headers.active || 0} active`, icon: Heading, gradient: 'linear-gradient(135deg, #6366f1, #818cf8)', bgColor: 'rgba(99, 102, 241, 0.06)', borderColor: 'rgba(99, 102, 241, 0.12)' },
    { title: 'Templates', value: templates.total || 0, sub: `${templates.active || 0} active`, icon: FileText, gradient: 'linear-gradient(135deg, #059669, #34d399)', bgColor: 'rgba(16, 185, 129, 0.06)', borderColor: 'rgba(16, 185, 129, 0.12)' },
    { title: 'Total SMS', value: (sms.total || 0), sub: `${todayTotal} today`, icon: MessageSquare, gradient: 'linear-gradient(135deg, #f97316, #fb923c)', bgColor: 'rgba(249, 115, 22, 0.06)', borderColor: 'rgba(249, 115, 22, 0.12)', label: 'This Month' },
    { title: 'Platform Balance', value: formatIndianCurrency(recharge.platform_balance || 0), sub: `${recharge.total_recharges || 0} recharges`, icon: Wallet, gradient: 'linear-gradient(135deg, #16a34a, #4ade80)', bgColor: 'rgba(22, 163, 74, 0.06)', borderColor: 'rgba(22, 163, 74, 0.12)' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Master Admin Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Platform overview &amp; real-time activity
            {lastUpdated && <span className="ml-2">· Updated {lastUpdated.toLocaleTimeString()}</span>}
            {fromCache && <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-indigo-50 text-indigo-500 border border-indigo-100">CACHED</span>}
          </p>
        </div>
        <button
          onClick={() => fetchStats(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white shadow-sm transition-all hover:shadow-md disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => <KPISkeleton key={i} />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {kpiCards.map((card, i) => (
            <div key={i} className="rounded-xl p-4 transition-all duration-200 hover:shadow-md" style={{
              background: card.bgColor, border: `1px solid ${card.borderColor}`,
            }}>
              <div className="flex items-center justify-between mb-2 gap-1">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: card.gradient }}>
                  <card.icon className="w-4 h-4 text-white" />
                </div>
                <span className="text-[9px] lg:text-[10px] font-semibold text-gray-400 uppercase tracking-wide text-right leading-tight truncate">{card.title}</span>
              </div>
              <div className="text-xl font-bold text-gray-900 truncate">{typeof card.value === 'number' ? card.value.toLocaleString() : card.value}</div>
              <div className="text-[11px] text-gray-500 mt-1 truncate">
                {card.label && <span className="text-[9px] font-bold text-indigo-400 uppercase mr-1">{card.label}</span>}
                {card.sub}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SMS Delivery Status (Today) + SMS Trend (Last 7 Days) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SMS Delivery Status — TODAY only */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4" style={{ color: '#6366f1' }} /> SMS Delivery Status
            <span className="ml-auto text-[9px] font-bold text-indigo-400 uppercase px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-100">Today</span>
          </h3>
          {loading ? (
            <div className="space-y-3"><Skeleton className="w-full h-16" /><Skeleton className="w-full h-16" /></div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Delivered', value: sms.delivered || 0, icon: CheckCircle, color: '#059669', bg: 'rgba(16, 185, 129, 0.08)' },
                  { label: 'Failed', value: sms.failed || 0, icon: XCircle, color: '#dc2626', bg: 'rgba(239, 68, 68, 0.08)' },
                  { label: 'Pending', value: sms.pending || 0, icon: Clock, color: '#d97706', bg: 'rgba(245, 158, 11, 0.08)' },
                ].map((sc, i) => (
                  <div key={i} className="rounded-lg p-3 text-center" style={{ background: sc.bg }}>
                    <sc.icon className="w-5 h-5 mx-auto mb-1" style={{ color: sc.color }} />
                    <div className="text-lg font-bold text-gray-900">{sc.value.toLocaleString()}</div>
                    <div className="text-xs text-gray-500">{sc.label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-lg p-3 flex items-center gap-3" style={{ background: 'rgba(16, 185, 129, 0.06)' }}>
                  <ArrowUpRight className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="text-lg font-bold text-green-700">{todayDeliveryRate}%</div>
                    <div className="text-xs text-green-600">Delivery Rate</div>
                  </div>
                </div>
                <div className="rounded-lg p-3 flex items-center gap-3" style={{ background: 'rgba(239, 68, 68, 0.06)' }}>
                  <ArrowDownRight className="w-5 h-5 text-red-600" />
                  <div>
                    <div className="text-lg font-bold text-red-700">{todayFailureRate}%</div>
                    <div className="text-xs text-red-600">Failure Rate</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* SMS 7-Day Trend — recharts */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" style={{ color: '#6366f1' }} /> SMS Trend (Last 7 Days)
            <span className="ml-auto text-[9px] font-bold text-indigo-400 uppercase px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-100">Summary</span>
          </h3>
          {loading ? (
            <Skeleton className="w-full h-48" />
          ) : (
            <SMSTrendChart data={sms.trend_7d || []} />
          )}
        </div>
      </div>

      {/* Middle Row: SMS by Channel + Template Types + User Roles */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* SMS by Channel — TODAY */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4" style={{ color: '#f59e0b' }} /> SMS by Channel
            <span className="ml-auto text-[9px] font-bold text-amber-500 uppercase px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100">Today</span>
          </h3>
          {loading ? (
            <div className="space-y-3"><Skeleton className="w-full h-6" /><Skeleton className="w-full h-6" /><Skeleton className="w-full h-6" /></div>
          ) : (
            <div className="space-y-3">
              {(sms.by_channel || []).map((ch, i) => {
                const pct = channelTotal > 0 ? ((ch.count / channelTotal) * 100).toFixed(1) : 0;
                const colors = { SMPP: '#3b82f6', HTTP: '#22c55e', GUI: '#8b5cf6', UNKNOWN: '#9ca3af' };
                const color = colors[ch.channel] || '#6b7280';
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }}></span>
                        <span className="text-sm font-medium text-gray-700">{ch.channel}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">{pct}%</span>
                        <span className="text-sm font-bold text-gray-900 w-14 text-right">{ch.count.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }}></div>
                    </div>
                  </div>
                );
              })}
              {(!sms.by_channel || sms.by_channel.length === 0) && <p className="text-sm text-gray-400 text-center py-4">No data for today</p>}
              {channelTotal > 0 && (
                <div className="pt-2 mt-1" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500">Total Today</span>
                    <span className="text-sm font-bold text-gray-900">{channelTotal.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Template Types */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" style={{ color: '#059669' }} /> Templates by Type
          </h3>
          <div className="space-y-3">
            {(templates.by_type || []).map((tt, i) => {
              const colors = ['#6366f1', '#22c55e', '#8b5cf6', '#f97316'];
              const pct = templates.total > 0 ? ((tt.count / templates.total) * 100).toFixed(1) : 0;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700 capitalize">{tt.type.replace('_', ' ')}</span>
                    <span className="text-sm font-bold text-gray-900">{tt.count}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, background: colors[i % colors.length] }}></div>
                  </div>
                </div>
              );
            })}
            {(!templates.by_type || templates.by_type.length === 0) && <p className="text-sm text-gray-400 text-center py-4">No data</p>}
          </div>
        </div>

        {/* User Roles */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" style={{ color: '#6366f1' }} /> Users by Role
          </h3>
          <div className="space-y-3">
            {(users.by_role || []).map((ur, i) => {
              const colors = ['#6366f1', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444'];
              const pct = users.total > 0 ? ((ur.count / users.total) * 100) : 0;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700 capitalize">{ur.role.replace('_', ' ')}</span>
                    <span className="text-sm font-bold text-gray-900">{ur.count}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, background: colors[i % colors.length] }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom Row: Recent Activity Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent SMS */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" style={{ color: '#3b82f6' }} /> Recent SMS Activity
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: 'rgba(99, 102, 241, 0.08)' }}>
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">User</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Mobile</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Channel</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Status</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Time</th>
                </tr>
              </thead>
              <tbody>
                {(s.recent_sms || []).map((smsItem, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50" style={{ borderColor: 'rgba(99, 102, 241, 0.04)' }}>
                    <td className="py-2 px-2 font-medium text-gray-900">{smsItem.username}</td>
                    <td className="py-2 px-2 text-gray-600 font-mono text-xs">{maskMobile(smsItem.mobile)}</td>
                    <td className="py-2 px-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${smsItem.channel === 'SMPP' ? 'bg-blue-100 text-blue-700' : smsItem.channel === 'HTTP' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
                        {smsItem.channel}
                      </span>
                    </td>
                    <td className="py-2 px-2"><StatusBadge status={smsItem.status} /></td>
                    <td className="py-2 px-2 text-gray-500 text-xs">{formatTime(smsItem.created_at)}</td>
                  </tr>
                ))}
                {(!s.recent_sms || s.recent_sms.length === 0) && (
                  <tr><td colSpan="5" className="py-6 text-center text-gray-400">No recent SMS</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Logins — with date and time */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Eye className="w-4 h-4" style={{ color: '#8b5cf6' }} /> Recent Login Activity
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: 'rgba(99, 102, 241, 0.08)' }}>
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">User</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Event</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Status</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">IP</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {(s.recent_logins || []).map((login, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50" style={{ borderColor: 'rgba(99, 102, 241, 0.04)' }}>
                    <td className="py-2 px-2 font-medium text-gray-900">{login.username}</td>
                    <td className="py-2 px-2 text-gray-600 capitalize">{login.event_type}</td>
                    <td className="py-2 px-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${login.event_status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {login.event_status}
                      </span>
                    </td>
                    <td className="py-2 px-2">
                      <IPCell ip={login.ip_address} userAgent={login.user_agent} details={login.details} username={login.username} onShowPopup={setIpPopup} />
                    </td>
                    <td className="py-2 px-2 text-gray-500 text-xs whitespace-nowrap">
                      {login.formatted_time || formatDateTime(login.created_at)}
                    </td>
                  </tr>
                ))}
                {(!s.recent_logins || s.recent_logins.length === 0) && (
                  <tr><td colSpan="5" className="py-6 text-center text-gray-400">No recent logins</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* IP Detail Popup */}
      {ipPopup && <IPPopup data={ipPopup} onClose={() => setIpPopup(null)} />}

      {/* Bottom: Top Users + Recent Recharges + Workers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Users by SMS — Last 7 Days */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" style={{ color: '#f97316' }} /> Top Users by SMS Volume
            <span className="ml-auto text-[9px] font-bold text-orange-500 uppercase px-1.5 py-0.5 rounded bg-orange-50 border border-orange-100">7 Days</span>
          </h3>
          <div className="space-y-3">
            {(s.top_users || []).map((tu, i) => {
              const total = tu.sms_count || 0;
              const delPct = total > 0 ? ((tu.delivered / total) * 100).toFixed(0) : 0;
              const medals = ['#f59e0b', '#9ca3af', '#cd7f32'];
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: medals[i] || '#e5e7eb', color: i >= 3 ? '#6b7280' : '#fff' }}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 truncate">{tu.username}</span>
                      <span className="text-sm font-bold text-gray-900">{total.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full transition-all" style={{ width: `${delPct}%`, background: 'linear-gradient(90deg, #22c55e, #4ade80)' }}></div>
                      </div>
                      <span className="text-xs text-gray-400 w-8 text-right">{delPct}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
            {(!s.top_users || s.top_users.length === 0) && <p className="text-sm text-gray-400 text-center py-4">No data</p>}
          </div>
        </div>

        {/* Recent Recharges & Deductions — from balance_transactions */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Wallet className="w-4 h-4" style={{ color: '#16a34a' }} /> Recent Recharges & Deductions
            <span className="ml-auto text-[9px] font-bold text-indigo-400 uppercase px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-100">Latest 10</span>
          </h3>
          <div className="space-y-2">
            {(recharge.recent || []).map((r, i) => {
              const isRecharge = r.reference_type === 'RECHARGE';
              return (
                <div key={i} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: 'rgba(99, 102, 241, 0.05)' }}>
                  <div className="min-w-0 flex-1 mr-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">{r.username}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${isRecharge ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                        {r.reference_type}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5 truncate">
                      {r.formatted_time || formatDateTime(r.created_at)}
                      {r.added_by && <span className="ml-1 text-indigo-500">by {r.added_by}</span>}
                    </div>
                    {r.description && <div className="text-[10px] text-gray-400 truncate">{r.description}</div>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className={`text-sm font-bold ${isRecharge ? 'text-green-600' : 'text-red-600'}`}>
                      {isRecharge ? '+' : '-'}{formatIndianCurrency(Math.abs(r.amount))}
                    </div>
                    <div className="text-[10px] text-gray-400">Bal: {formatIndianCurrency(r.balance_after || 0)}</div>
                  </div>
                </div>
              );
            })}
            {(!recharge.recent || recharge.recent.length === 0) && <p className="text-sm text-gray-400 text-center py-4">No recent transactions</p>}
          </div>
        </div>

        {/* Worker Status */}
        <div className="bg-white rounded-xl p-5 transition-all" style={cardStyle}>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Server className="w-4 h-4" style={{ color: '#6b7280' }} /> Worker Status
          </h3>
          <div className="space-y-3">
            {(s.workers || []).map((w, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: 'rgba(99, 102, 241, 0.05)' }}>
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${w.status === 'running' ? 'bg-green-500' : w.status === 'stopped' ? 'bg-red-500' : 'bg-yellow-500'}`}></span>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{w.name}</div>
                    <div className="text-xs text-gray-400">{w.type}</div>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${w.status === 'running' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {w.status}
                  </span>
                  {w.last_heartbeat && <div className="text-xs text-gray-400 mt-1">{formatTime(w.last_heartbeat)}</div>}
                </div>
              </div>
            ))}
            {(!s.workers || s.workers.length === 0) && <p className="text-sm text-gray-400 text-center py-4">No workers registered</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

// SMS Trend Chart using Recharts
const SMSTrendChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="flex items-center justify-center h-48 text-gray-400 text-sm">No trend data available</div>;
  }

  const chartData = data.map(d => ({
    date: new Date(d.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    day: new Date(d.date).toLocaleDateString('en-IN', { weekday: 'short' }),
    Total: d.total || 0,
    Delivered: d.delivered || 0,
    Failed: d.failed || 0,
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2 text-xs">
        <p className="font-semibold text-gray-800 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>
            {p.name}: <span className="font-bold">{p.value.toLocaleString()}</span>
          </p>
        ))}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={chartData} barGap={2}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(99, 102, 241, 0.08)" />
        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={40} />
        <Tooltip content={<CustomTooltip />} />
        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
        <Bar dataKey="Delivered" fill="#22c55e" radius={[3, 3, 0, 0]} />
        <Bar dataKey="Failed" fill="#ef4444" radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Helper: Status Badge
const StatusBadge = ({ status }) => {
  const config = {
    DELIVERED: { bg: 'bg-green-100', text: 'text-green-700' },
    SENT: { bg: 'bg-blue-100', text: 'text-blue-700' },
    SUBMITTED: { bg: 'bg-blue-100', text: 'text-blue-700' },
    PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
    FAILED: { bg: 'bg-red-100', text: 'text-red-700' },
    REJECTED: { bg: 'bg-red-100', text: 'text-red-700' },
  };
  const c = config[status] || { bg: 'bg-gray-100', text: 'text-gray-700' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>{status}</span>;
};

// Helper: Mask mobile number
const maskMobile = (mobile) => {
  if (!mobile || mobile.length < 6) return mobile;
  return mobile.slice(0, 3) + '****' + mobile.slice(-3);
};

// Helper: Format time (relative)
const formatTime = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// Helper: Format date and time (absolute)
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Helper: IP Cell
const IPCell = ({ ip, userAgent, details, username, onShowPopup }) => {
  if (!ip || ip === '::1' || ip === '127.0.0.1') {
    return (
      <span className="text-gray-400 text-xs font-mono flex items-center gap-1">
        {ip || '-'}
        {ip && (
          <button onClick={() => onShowPopup({ ip, userAgent, details, username })} className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600" title="View details">
            <Info className="w-3 h-3" />
          </button>
        )}
      </span>
    );
  }
  const isLongIP = ip.length > 15;
  const displayIP = isLongIP ? ip.slice(0, 15) + '...' : ip;
  return (
    <span className="text-gray-600 text-xs font-mono flex items-center gap-1">
      {displayIP}
      <button onClick={() => onShowPopup({ ip, userAgent, details, username })} className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-blue-600" title="View IP details">
        <Info className="w-3 h-3" />
      </button>
    </span>
  );
};

// Helper: IP Detail Popup Modal
const IPPopup = ({ data, onClose }) => {
  if (!data) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-500" /> Connection Details
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-3">
          {data.username && (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">User</div>
              <div className="text-sm font-medium text-gray-900">{data.username}</div>
            </div>
          )}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">IP Address</div>
            <div className="text-sm font-mono bg-gray-50 rounded-lg px-3 py-2 text-gray-800 break-all select-all">{data.ip || '-'}</div>
          </div>
          {data.userAgent && (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">User Agent</div>
              <div className="text-xs font-mono bg-gray-50 rounded-lg px-3 py-2 text-gray-600 break-all max-h-24 overflow-y-auto">{data.userAgent}</div>
            </div>
          )}
          {data.details && (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">Details</div>
              <div className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">{data.details}</div>
            </div>
          )}
        </div>
        <button onClick={onClose} className="mt-4 w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors">Close</button>
      </div>
    </div>
  );
};

export default MasterDashboard;
